<template>
  <settings-modal
    class="tier-wd-rule-editor-modal t-tier-wd-rule-editor-modal"
  >
    <span
      slot="heading"
      class="font-weight-bold"
    >
      <span>Edit</span>
      <v-tooltip bottom>
        <a slot="activator">{{ ruleCode }}</a>
        <span class="">{{ tooltip }}</span>
      </v-tooltip>
      <span>for tier "{{ tier }}"</span>
    </span>

    <v-layout row>
      <v-flex>
        <div class="v-label theme--light mb-2">Rule status</div>
        <v-switch
          v-model="form.disabled"
          class="pt-0 mt-0"
          color="primary"
          :true-value="false"
          :false-value="true"
          :label="form.disabled ? 'Disabled' : 'Enabled'"
          :error-messages="getValidationErrorMessage('disabled')"
        />
      </v-flex>
    </v-layout>

    <v-layout
      row
    >
      <v-flex>
        <v-text-field
          v-model="form.iban"
          class="tier-wd-rule-editor-modal__iban-input pt-0 mt-0"
          label="Add IBAN to the whitelist"
          hint="Please press Enter or '+' to add IBAN"
          persistent-hint
          :error-messages="getValidationErrorMessage('ibanWhitelist')"
          @keyup.enter="onIbanFieldEnterKeyup"
        >
          <template slot="append">
            <v-btn
              small
              icon
              color="primary"
              style="margin: 4px 8px;"
              @click="onIbanAddButtonClick"
            ><v-icon>add</v-icon></v-btn>
          </template>
        </v-text-field>

        <data-heading
          heading="IBAN whitelist"
          style="font-size: 16px; margin-top: 10px;"
        />
        <div class="pt-2 mb-4">
          <template v-if="ibanWhitelist.length > 0">
            <v-chip
              v-for="iban in ibanWhitelist"
              :key="iban"
              close
              @input="onIbanRemoveIconClick(iban)"
            >{{ iban }}</v-chip>
          </template>
          <div v-else>None added</div>
        </div>
      </v-flex>
    </v-layout>

    <error-alert :error="error" />

    <template slot="actions">
      <v-btn
        class="t-confirm-button"
        color="primary"
        medium
        flat
        :disabled="loading"
        @click="onConfirmButtonClick"
      >Save changes</v-btn>

      <v-btn
        class="t-cancel-button"
        color="primary"
        medium
        flat
        @click="onCloseButtonClick"
      >cancel</v-btn>
    </template>
  </settings-modal>
</template>

<script>
  // Utils
  import { isEmptyValue } from '@/utils/Data'

  // Events
  import { EVENT_PROFILE_WATCHDOG_RULE_UPDATE } from '@/events/profileWatchdogRules'

  // Services
  import ApiRequest from '@/services/ApiRequests/ApiRequest'
  import WatchdogApi from '@/services/Api/WatchdogApi'

  // Mixins
  import form from '@/mixins/form'
  import modal from '@/mixins/modal'
  import flashMessages from '@/mixins/flashMessages'

  // Components
  import SettingsModal from '@/components/modals/SettingsModal'
  import DataHeading from '@/components/ui/Data/DataHeading'
  import ErrorAlert from '@/components/ui/ErrorAlert'

  export default {
    name: 'TierWatchdogRuleEditorModal',
    components: {
      SettingsModal,
      DataHeading,
      ErrorAlert
    },
    mixins: [form, modal, flashMessages],
    data () {
      return {
        form: {
          disabled: null,
          iban: null
        },
        ibanWhitelist: [],
        updateProfileRulesApiRequest: new ApiRequest(WatchdogApi.updateProfileRules)
      }
    },
    computed: {
      tier () {
        return this.modalData.tier
      },
      tooltip () {
        return this.modalData.tooltip
      },
      ruleCode () {
        return this.modalData.ruleCode
      },
      ruleGroupId () {
        return this.modalData.ruleGroupId
      },
      ruleIds () {
        return this.modalData.ruleIds
      },
      profileId () {
        return this.modalData.profileId
      },
      error () {
        return this.updateProfileRulesApiRequest.error
      },
      loading () {
        return this.updateProfileRulesApiRequest.loading
      }
    },
    created () {
      this.setFieldValue('disabled', this.modalData.ruleDisabled)
      this.setIbanWhitelist(this.modalData.ibanWhitelist)
    },
    methods: {
      onIbanFieldEnterKeyup () {
        const newIban = this.parseIban(this.parsedFormInput.iban)

        if (newIban == null) {
          return
        }

        this.addIbanToWhitelist(newIban)
        this.setFieldValue('iban', null)
      },
      onIbanAddButtonClick () {
        const newIban = this.parseIban(this.parsedFormInput.iban)

        if (newIban == null) {
          return
        }

        this.addIbanToWhitelist(newIban)
        this.setFieldValue('iban', null)
      },
      onIbanRemoveIconClick (iban) {
        this.removeIbanToWhitelist(iban)
      },
      onConfirmButtonClick () {
        this.updateProfileRulesApiRequest
          .execute(
            this.profileId,
            {
              ruleGroupId: this.ruleGroupId,
              ruleIds: this.ruleIds,
              disabled: this.parsedFormInput.disabled,
              ibanWhitelist: this.ibanWhitelist
            }
          )
          .then(() => {
            this.$eventHub.$emit(EVENT_PROFILE_WATCHDOG_RULE_UPDATE)

            this.showSuccessFlashMessage(`${this.ruleCode} has been successfully updated.`)
          })
          .catch(() => {})
      },
      parseIban (iban) {
        if (!isEmptyValue(iban)) {
          return iban.replaceAll(' ', '')
        }
      },
      addIbanToWhitelist (iban) {
        if (!this.ibanWhitelist.includes(iban)) {
          this.setIbanWhitelist([...this.ibanWhitelist, iban])
        }
      },
      removeIbanToWhitelist (iban) {
        this.setIbanWhitelist(this.ibanWhitelist.filter((ibanInList) => ibanInList !== iban))
      },
      setIbanWhitelist (ibanWhitelist) {
        this.$set(this, 'ibanWhitelist', ibanWhitelist)
      },
      onCloseButtonClick () {
        this.closeModal()
      }
    }
  }
</script>

<style lang="scss">
  .tier-wd-rule-editor-modal {
    &__iban-input {
      .v-messages__message {
        color: #ff5252;
      }
    }
  }
</style>
